// Helpers shared by the generation-config UI (checkpoint cards + custom rules).

// Resolve a value's display label from a backend {value, label} option list.
export const option_label = (options, value) => {
  const found = (options || []).find((o) => o.value === value);
  return found ? found.label : value;
};

// Render a custom rule's summary string — mirrors the backend's
// buildCustomName, which names the generated checklists, e.g.
// "Custom: No bookings in last 2 weeks + Signup: Last month + Plan type: PC".
export const build_rule_summary = (rule, meta) => {
  const m = meta || {};
  const f = rule.filters || {};
  const parts = [`Custom: ${option_label(m.condition_options, rule.condition)}`];
  if (f.signup_window && f.signup_window !== 'all_time') {
    parts.push(`Signup: ${option_label(m.signup_options, f.signup_window)}`);
  }
  if (f.prepay_type && f.prepay_type.length) {
    parts.push(`Prepay: ${f.prepay_type.map((v) => option_label(m.prepay_options, v)).join(', ')}`);
  }
  if (f.plan_type && f.plan_type.length) {
    parts.push(`Plan type: ${f.plan_type.join(', ')}`);
  }
  if (f.account_status && f.account_status.length) {
    parts.push(`Status: ${f.account_status.join(', ')}`);
  }
  return parts.join(' + ');
};

// Human description of a "No + conditions" checkpoint rule for the card hint
// line, e.g. "Generates only for members matching: Prepay Monthly AND Plan PC".
export const conditions_summary = (conditions, meta) => {
  const m = meta || {};
  const c = conditions || {};
  const parts = [];
  if (c.prepay_type && c.prepay_type.length) {
    parts.push(`Prepay ${c.prepay_type.map((v) => option_label(m.prepay_options, v)).join('/')}`);
  }
  if (c.plan_type && c.plan_type.length) {
    parts.push(`Plan ${c.plan_type.join('/')}`);
  }
  if (c.account_status && c.account_status.length) {
    parts.push(`Status ${c.account_status.join('/')}`);
  }
  return parts.join(' AND ');
};

// Conditions a membership lifecycle event fires in real time; only these can be
// instant (goserver instantConditions).
export const INSTANT_CONDITIONS = [
  'membership_cancelled',
  'membership_paused',
  'pause_payment_failure'
];

export const PAYMENT_FAILURE_CONDITION = 'pause_payment_failure';

export const DEFAULT_PAYMENT_FAILURE_TRIGGER = 'terminal';

export const PAYMENT_FAILURE_TRIGGER_OPTIONS = [
  { value: 'every_attempt', label: 'Every failed renewal' },
  { value: 'terminal', label: 'Only when membership pauses after final failure' }
];

// goserver bounds (config.go).
export const DEFAULT_DEDUP_WINDOW_HOURS = 1;
export const MAX_DEDUP_WINDOW_HOURS = 720;
export const MAX_EXPIRY_MINUTES = 10080;
// goserver DefaultExpiryMinutes; meta.default_expiry_minutes wins when present.
export const FALLBACK_EXPIRY_MINUTES = 60;

// meta.instant_conditions (server) wins; the constant covers an older server.
export const instant_conditions = (meta) =>
  meta && Array.isArray(meta.instant_conditions) && meta.instant_conditions.length
    ? meta.instant_conditions
    : INSTANT_CONDITIONS;

export const can_be_instant = (condition, meta) => instant_conditions(meta).includes(condition);

export const default_instant_expiry_minutes = (meta) => {
  const n = parseInt(meta && meta.default_expiry_minutes, 10);
  return Number.isNaN(n) || n <= 0 ? FALLBACK_EXPIRY_MINUTES : Math.min(n, MAX_EXPIRY_MINUTES);
};

// Turning instant on prefills the default. The expiry field is hidden while the
// rule is not instant, so a 0 there was never a deliberate "never".
export const with_instant = (rule, on, meta) => {
  const next = { ...rule, is_instant: on };
  const current = parseInt(rule.expiry_minutes, 10);
  if (on && (Number.isNaN(current) || current <= 0)) {
    next.expiry_minutes = default_instant_expiry_minutes(meta);
  }
  return next;
};

// Blank stays blank (expiry: none; dedup: server default); otherwise clamp.
export const clamp_optional_int = (raw, min, max) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return null;
  return Math.min(Math.max(n, min), max);
};

// Default shape for a freshly-added custom rule (id assigned by the server on
// save).
export const new_custom_rule = (meta) => ({
  id: '',
  enabled: true,
  is_instant: false,
  expiry_minutes: default_instant_expiry_minutes(meta),
  dedup_window_hours: DEFAULT_DEDUP_WINDOW_HOURS,
  payment_failure_trigger: '',
  condition: 'cancel_noshow_over_25pct',
  filters: {
    signup_window: 'all_time',
    prepay_type: [],
    plan_type: [],
    account_status: []
  }
});

// goserver decodes these as ints, so a cleared box must not go out as "".
// Blank is omitted so the server default applies (expiry 60, dedup 1); 0 is
// sent as 0 (expiry: never; dedup: no window).
export const custom_rules_payload = (rules) =>
  (rules || []).map((r) => {
    const out = { ...r, is_instant: !!r.is_instant };
    const expiry = parseInt(r.expiry_minutes, 10);
    if (Number.isNaN(expiry)) delete out.expiry_minutes;
    else out.expiry_minutes = expiry;
    const dedup = parseInt(r.dedup_window_hours, 10);
    if (Number.isNaN(dedup)) delete out.dedup_window_hours;
    else out.dedup_window_hours = dedup;
    if (r.payment_failure_trigger === undefined || r.payment_failure_trigger === null) {
      out.payment_failure_trigger = '';
    }
    return out;
  });
